import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

export const useDebounce = (value: any, delay: number = 500) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);
  return debouncedValue;
};

export function useIsMounted() {
  const isMounted = useRef(false);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);
  return useCallback(() => isMounted.current, []);
}

export const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

export function useInterval(callback: () => void, delay: number | null) {
  const savedCallback = useRef(callback);

  // Remember the latest callback if it changes.
  useIsomorphicLayoutEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval.
  useEffect(() => {
    // Don't schedule if no delay is specified.
    // Note: 0 is a valid value for delay.
    if (delay === null) {
      return;
    }

    const id = setInterval(() => {
      savedCallback.current();
    }, delay);

    return () => {
      clearInterval(id);
    };
  }, [delay]);
}

export function useClickOutside<T extends HTMLElement = HTMLElement>(
  callback: () => void,
  enabled: boolean = true,
): React.RefObject<T | null> {
  const ref = useRef<T | null>(null);
  const savedCallback = useRef<() => void>(callback);

  // Remember the latest callback if it changes.
  useIsomorphicLayoutEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const handleClick = (event: MouseEvent): void => {
      const target = event.target;
      const currentRef = ref.current;
      const currentCallback = savedCallback.current;

      if (!currentRef || !target || !(target instanceof Node) || !currentCallback) {
        return;
      }

      // Check if click is inside the ref element
      if (currentRef.contains(target)) {
        return;
      }

      // Only check for positioned elements if the target itself or its immediate parent is positioned
      // and might be a dropdown/popup related to our component
      const targetElement = target as Element;
      const targetStyle = window.getComputedStyle(targetElement);
      const parentElement = targetElement.parentElement;
      const parentStyle = parentElement ? window.getComputedStyle(parentElement) : null;

      // Check if the target or its immediate parent is positioned and overlaps with our ref
      if (
        targetStyle.position === 'absolute' ||
        targetStyle.position === 'fixed' ||
        (parentStyle && (parentStyle.position === 'absolute' || parentStyle.position === 'fixed'))
      ) {
        const elementToCheck =
          targetStyle.position === 'absolute' || targetStyle.position === 'fixed'
            ? targetElement
            : parentElement!;

        const refRect = currentRef.getBoundingClientRect();
        const elementRect = elementToCheck.getBoundingClientRect();

        // If the positioned element overlaps with our ref area, it might be related
        if (
          elementRect.left < refRect.right &&
          elementRect.right > refRect.left &&
          elementRect.top < refRect.bottom &&
          elementRect.bottom > refRect.top
        ) {
          return; // Don't trigger callback for overlapping positioned elements
        }
      }

      // If we get here, the click is truly outside
      currentCallback();
    };

    // Add event listener to document
    document.addEventListener('mousedown', handleClick);

    return (): void => {
      document.removeEventListener('mousedown', handleClick);
    };
  }, [enabled]);

  return ref;
}
